import * as React from 'react'
import { Editor } from '@tinymce/tinymce-react'
const throttle = require('lodash.throttle')

import {
  consumeInjectedScript,
  InjectedScriptProvider,
} from '../state/InjectedScript'

import {
  DEFAULT_PLUGINS,
  generateExternalPlugins,
  SCRIPT_ID,
  THROTTLE_DURATION,
  TINY_MCE_CONFIG,
  TINY_MCE_SOURCE,
  TINY_MCE_VERSION,
} from '../utils/TinyMCE'

interface TextEditorProps {
  initialValue?: string
  onChange?: (content: string) => void
  plugins?: Array<string>
}

const Wrapped = React.createContext({ wrapped: false })

export const TextEditor: React.SFC<TextEditorProps> = ({
  initialValue = '',
  onChange = content => {},
  plugins = DEFAULT_PLUGINS,
  ...props
}) => {
  const { isLoaded, setLoaded } = consumeInjectedScript()

  const onEditorChange = throttle(
    (_: any, editor: any) => onChange(editor.getContent()),
    THROTTLE_DURATION
  )

  const editor = (
    <Editor
      init={{
        ...TINY_MCE_CONFIG,
        external_plugins: generateExternalPlugins(plugins),
      }}
      initialValue={initialValue}
      onChange={onEditorChange}
      onKeyUp={onEditorChange}
      {...props}
    />
  )

  return (
    <Wrapped.Consumer>
      {({ wrapped }) => (wrapped ? isLoaded && editor : null)}
    </Wrapped.Consumer>
  )
}

interface ProviderProps {
  children?: React.ReactNode
}

export const TextEditorProvider: React.SFC<ProviderProps> = ({ children }) => (
  <InjectedScriptProvider
    id={SCRIPT_ID}
    src={`${TINY_MCE_SOURCE}/${TINY_MCE_VERSION}/tinymce.min.js`}
  >
    <Wrapped.Provider value={{ wrapped: true }}>{children}</Wrapped.Provider>
  </InjectedScriptProvider>
)

TextEditor.displayName = 'TextEditor'

export default TextEditor
